import { useForm } from 'react-hook-form';
import sweetAlert from 'sweetalert2';
import { gql, useMutation } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import { isLoggedInVar, jwtToken } from '../../apollo';
import { SESSIONSTORAGE_TOKEN } from '../../components/constants';
import {
    ILoginMutationInput,
    ILoginMutationOutput,
    VerifyTokenMutationInput,
    VerifyTokenMutationOutput,
} from './dtos/login-mutation.dto';
import { IFindPasswordInput, IFindPasswordOutput } from './dtos/find-password-mutation.dto';
//interface는 I로 시작하게 하면 직관적으로 알 수 있다.

//mutation LoginMutation($email: String!, $password: String!) 이 줄은 모두 frontend를 위한거다
//mutation 뒤의 이름은 Front에서만 확인하는 변수다. $뒤에 입력하는 이름은 아폴로에게 알려주는 변수명이다.
//playground에 입력하는대로 그대로 적으면 된다.
/**
 * @alias 로그인 mutation
 */
const LOGIN_MUTATION = gql`
    mutation loginMutation($loginInput: LoginInput!) {
        login(input: $loginInput) {
            success
            message
            token
            pwdFailCnt
        }
    }
`;

/**
 * @alias 비밀번호 찾기 - 이메일 인증 보내기 mutation
 */
const FIND_PASSWORD_MUTATION = gql`
    mutation findPasswordMutation($findPasswordInput: FindPasswordInput!) {
        findPassword(input: $findPasswordInput) {
            success
            message
        }
    }
`;

/**
 * @alias 토큰 검사 하기 - 토큰이 유효한지 확인 mutation
 */
const VERIFY_TOKEN_MUTATION = gql`
    mutation verifyTokenMutation($verifyTokenInput: VerifyTokenInput!) {
        verifyToken(input: $verifyTokenInput) {
            success
            message
        }
    }
`;

/**
 * @alias login form interface
 */
interface ILoginForm {
    email: string;
    password: string;
}

/**
 * @alias 비밀번호 찾기 - 이메일 인증 interface
 */
interface IFindPassword extends Partial<Pick<ILoginForm, 'email'>> {}

//이메일 형식 체크 정규식
const reg_email = /^([0-9a-zA-Z_\.-]+)@([0-9a-zA-Z_-]+)(\.[0-9a-zA-Z_-]+){1,2}$/;

// 비밀번호 체크 정규식 (영문+숫자+특문조합 8자 이상)
const reg_pw = /^(?=.*[a-zA-Z])(?=.*[!@#$%^*+=\-,\\.<>/\\?~\\(\\)\\_\\[\]\\{\\}\\|;:])(?=.*[0-9]).{8,}$/;

/**
 *
 * @frontend d
 */
function Login() {
    const history = useHistory();
    //console.log(watch('email')); //ref={register}로 등록해주면 email 이라는 input name의 Element를 관찰합니다.
    //Email 란에 입력하는대로 console.log가 열심히 찍힘 (ex:비밀번호 몇자이상 등 유효성 검사를 위해 watch를 해야함)
    const { register, watch, errors, handleSubmit, getValues } = useForm<ILoginForm>();

    //#region 이벤트 처리
    /**
     * @alias 로그인 완료 함수
     */
    const onCompleted = async (data: ILoginMutationOutput) => {
        const {
            login: { success, message, token, pwdFailCnt },
        } = data;

        if (success && token) {
            sessionStorage.setItem(SESSIONSTORAGE_TOKEN, token);
            jwtToken(token);
            isLoggedInVar(true);

            // 토큰 검사 뮤테이션 실행
            const result = await verifyTokenMutation({
                variables: {
                    verifyTokenInput: { token },
                },
            });
            if (result?.data?.verifyToken?.success) {
                await sweetAlert.fire({
                    title: '성공',
                    html: `${message}`,
                    icon: 'success',
                    confirmButtonText: '확인',
                });
                history.push('/');
            }
        } else {
            // 비밀번호 10회 오류 시 통합 계정이 잠기므로 비밀번호 찾기 모달창 열기
            if (pwdFailCnt && pwdFailCnt >= 10) {
                await showFindPw({ email: getValues('email') });
            } else {
                await sweetAlert.fire({
                    title: '실패',
                    html: `${message}`,
                    icon: 'error',
                });
            }
        }
    };

    const onError = async (error: any) => {
        // graphQLErrors가 있을 때 메시지 추출
        const message = error?.graphQLErrors?.[0]?.message || error?.message || '알 수 없는 에러가 발생했습니다.';

        await sweetAlert.fire({
            icon: 'error',
            title: '실패',
            html: message,
        });
    };

    /**
     * @alias 로그인 submit 함수
     */
    const onSubmit = () => {
        const { email, password } = getValues();
        loginMutation({
            variables: {
                loginInput: {
                    email,
                    password,
                },
            },
        });
    };

    /**
     * @alias 비밀번호 찾기 이메일 인증 이벤트
     */
    const onFindPw = async (e?: React.MouseEvent, input?: IFindPassword) => {
        e && e.preventDefault();

        const { value: email } = await sweetAlert.fire({
            title: `비밀번호 찾기`,
            input: `email`,
            inputPlaceholder: `이메일 주소를 입력해 주시기 바랍니다.`,
            // 로그인 실패 경로에서 들어왔을 경우 email 그대로 전달
            inputValue: input?.email ? input.email : '',
            // email 전달 했을 경우 수정 불가
            inputAttributes: input?.email ? { readonly: 'readonly' } : {},
            confirmButtonText: `인증메일 발송`,
        });

        // 이메일 입력 했을 경우 비밀번호 찾기용 이메일 인증 이벤트 ㄱㄱ
        if (email) {
            findPasswordMutation({
                variables: {
                    findPasswordInput: {
                        email,
                    },
                },
            });
        }
    };

    /**
     * @alias 비밀번호 찾기 > 이메일 인증 완료 이벤트
     */
    const onCompletedFindPassword = async (data: IFindPasswordOutput) => {
        await sweetAlert.fire({
            title: '성공',
            html: `입력하신 ${getValues('email')}로 비밀번호 재설정 이메일이 전송되었습니다.<br/>
          이메일 인증하기 버튼을 눌러 비밀번호를 변경해주세요`,
            icon: 'success',
            confirmButtonText: '확인',
        });
    };

    /**
     * @alias 로그인 실패 시 비밀번호 찾기 모달창 열리는 이벤트
     * @description 로그인 실패시에는 email을 전달시켜주기 위함
     */
    const showFindPw = async ({ email }: IFindPassword) => {
        const result = await sweetAlert.fire({
            title: '실패',
            html: `비밀번호 10회 이상 오류로 계정이 잠겼습니다. <br />
  아래 비밀번호 찾기 버튼을 눌러 비밀번호를 변경해 주세요.`,
            icon: 'error',
            showCancelButton: true,
            confirmButtonText: '비밀번호 찾기',
            cancelButtonText: '취소',
        });

        if (result.value === true) {
            onFindPw(undefined, { email });
        }
    };
    //#endregion 이벤트 처리

    //#region query, mutation 모음
    /**
     * @alias 로그인 뮤테이션
     */
    const [loginMutation] = useMutation<ILoginMutationOutput, ILoginMutationInput>(LOGIN_MUTATION, {
        onCompleted,
        onError,
    });

    /**
     * @alias 비밀번호 찾기 - 이메일 인증 보내기 mutation
     */
    const [findPasswordMutation] = useMutation<IFindPasswordOutput, IFindPasswordInput>(FIND_PASSWORD_MUTATION, {
        onCompleted: onCompletedFindPassword,
        //onError,
    });

    /**
     * @alias 토큰 검증 하기 - 토큰이 유효한지 확인 mutation
     */
    const [verifyTokenMutation] = useMutation<VerifyTokenMutationOutput, VerifyTokenMutationInput>(
        VERIFY_TOKEN_MUTATION,
        {
            onError,
        },
    );

    //#endregion query, mutation 모음

    return (
        <div id="loginscss">
            <div id="containerH">
                <div className="loginArea">
                    <form className="loginForm" onSubmit={handleSubmit(onSubmit)}>
                        <div>
                            {/* <img alt="" src={iconUser} /> */}
                            <input
                                name="email"
                                type="email"
                                placeholder="이메일 아이디"
                                // defaultValue="test" //고정값
                                ref={register({ required: true, maxLength: 50, pattern: reg_email })} // 유효성 체크를 위한 조건을 넣어준다
                            />
                            {/* // 유효성 체크에 걸렸을 때 에러 문구를 설정해 준다 */}
                            {errors.email && errors.email.type === 'required' && <p>이메일은 필수 입력 항목입니다. </p>}
                            {errors.email && errors.email.type === 'maxLength' && (
                                <p>이메일의 최대 길이를 초과하였습니다.(최대 30자) </p>
                            )}
                            {errors.email && errors.email.type === 'pattern' && <p>올바른 이메일 형식이 아닙니다.</p>}
                        </div>
                        <div>
                            {/* <img alt="" src={iconPass} /> */}
                            <input
                                name="password"
                                type="password"
                                placeholder="비밀번호"
                                ref={register({ required: true, minLength: 8, pattern: reg_pw })}
                            />
                            {errors.password && errors.password.type === 'required' && (
                                <p>비밀번호는 필수 입력 항목입니다. </p>
                            )}
                            {errors.password && errors.password.type === 'minLength' && (
                                <p>비밀번호는 8자 이상 입력해 주시기 바랍니다. </p>
                            )}
                            {errors.password && errors.password.type === 'pattern' && (
                                <p>비밀번호는 영문+숫자+특수문자 조합 8자 이상 입력해 주시기 바랍니다. </p>
                            )}
                        </div>

                        <button type="submit" onClick={handleSubmit(onSubmit)}>
                            로그인
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default Login;
